import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import { useAccountCompanyId } from 'application/hooks';
import { useAppSelector } from '@shared/store';
import { selectCompanyIds, selectUserData } from '@entities/account';
import { DEFAULT_POINT_STATUSES } from 'application/pages/PointsPage/renderer/PointsToolbar/consts';

import {
  CompanyAdCampaignsByDaysEarlyReportMutation,
  useMediaCompanyAdCampaignsByDaysEarlyReportMutation,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { useAllPointList } from '@entities/point';

import { getReportAddressTags, mapPointToReportAddress } from '../ReportRequestForm/reportAddresses';
import {
  EMAIL_REGEXP,
  FIRST_REFETCH_INTERVAL,
  MAX_EXTERNAL_RECIPIENT_EMAILS_COUNT,
  MAX_REPORT_RECIPIENT_EMAILS_COUNT,
} from './consts';
import { TEXTS } from './texts';
import type { RequestAdCampaignsByDaysReportModalProps } from './types';
import {
  buildDailyReportInput,
  getDailyReportCalendarMaxDate,
  getDailyReportDateBounds,
  getDailyReportDateRangeError,
  getPreparedReportEmails,
} from './utils';

export const useRequestAdCampaignsByDaysReportViewModel = ({
  close,
  refetchReports,
}: RequestAdCampaignsByDaysReportModalProps & { close: () => void }) => {
  const userData = useAppSelector(selectUserData);
  const companyId = useAccountCompanyId();
  const storedCompanyIds = useAppSelector(selectCompanyIds);
  const [mutation, { loading }] = useMediaCompanyAdCampaignsByDaysEarlyReportMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const companyIds = useMemo(
    () => (storedCompanyIds.length > 0 ? storedCompanyIds : [companyId]),
    [companyId, storedCompanyIds]
  );
  const companyIdsKey = companyIds.join(',');
  const hasCompanyIds = companyIds.length > 0;
  const allPointList = useAllPointList({
    companyIds,
    playingStatuses: DEFAULT_POINT_STATUSES,
  });
  const requestPointsRef = useRef(allPointList.request);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedMediafileId, setSelectedMediafileId] = useState<string | null>(null);
  const [selectedAddressIds, setSelectedAddressIds] = useState<string[]>([]);
  const [isAddressSelectionTouched, setIsAddressSelectionTouched] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [externalRecipientEmails, setExternalRecipientEmails] = useState<string[]>([]);
  const [isExternalRecipientsInputVisible, setIsExternalRecipientsInputVisible] = useState(false);
  const { maxDate, minDate } = useMemo(() => getDailyReportDateBounds(), []);
  const calendarMaxDate = getDailyReportCalendarMaxDate(startDate, endDate, maxDate);
  const dateRangeError = startDate && endDate ? getDailyReportDateRangeError(startDate, endDate, maxDate) : null;

  useEffect(() => {
    requestPointsRef.current = allPointList.request;
  }, [allPointList.request]);

  useEffect(() => {
    if (!hasCompanyIds) {
      return;
    }

    requestPointsRef.current().catch(() => {
      toast.error(DEFAULT_ERROR_MESSAGE);
    });
  }, [companyIdsKey, hasCompanyIds]);

  const reportAddresses = useMemo(
    () => (allPointList.points || []).map(mapPointToReportAddress),
    [allPointList.points]
  );
  const reportAddressTags = useMemo(() => getReportAddressTags(reportAddresses), [reportAddresses]);
  const reportAddressIds = useMemo(() => reportAddresses.map((address) => address.id), [reportAddresses]);
  const isReportAddressesLoading = allPointList.isReloading || allPointList.loading;

  useEffect(() => {
    const addressIdSet = new Set(reportAddressIds);

    setSelectedAddressIds((currentAddressIds) => {
      const nextAddressIds = isAddressSelectionTouched
        ? currentAddressIds.filter((addressId) => addressIdSet.has(addressId))
        : reportAddressIds;

      return currentAddressIds.length === nextAddressIds.length &&
        currentAddressIds.every((addressId, index) => addressId === nextAddressIds[index])
        ? currentAddressIds
        : nextAddressIds;
    });
  }, [isAddressSelectionTouched, reportAddressIds]);

  const recipients = useMemo(() => {
    const users = userData?.mainCompany?.users || [];

    if (users.length) {
      return users;
    }

    return userData?.id && userData.email ? [{ id: userData.id, email: userData.email }] : [];
  }, [userData]);
  const recipientIds = useMemo(() => recipients.map((recipient) => recipient.id), [recipients]);

  useEffect(() => {
    setSelectedRecipientIds(recipientIds);
  }, [recipientIds]);

  const selectedRecipientEmails = useMemo(() => {
    const selectedIds = new Set(selectedRecipientIds);

    return recipients.filter((recipient) => selectedIds.has(recipient.id)).map((recipient) => recipient.email);
  }, [recipients, selectedRecipientIds]);
  const preparedReportEmails = useMemo(
    () => getPreparedReportEmails([...selectedRecipientEmails, ...externalRecipientEmails]),
    [externalRecipientEmails, selectedRecipientEmails]
  );
  const internalEmailSet = useMemo(
    () => new Set(recipients.map((recipient) => recipient.email.trim().toLowerCase()).filter(Boolean)),
    [recipients]
  );
  const invalidExternalRecipientEmails = useMemo(
    () => externalRecipientEmails.filter((email) => !EMAIL_REGEXP.test(email)),
    [externalRecipientEmails]
  );
  const externalRecipientsCount = useMemo(
    () => preparedReportEmails.filter((email) => !internalEmailSet.has(email.toLowerCase())).length,
    [internalEmailSet, preparedReportEmails]
  );
  const hasTooManyExternalRecipients = externalRecipientsCount > MAX_EXTERNAL_RECIPIENT_EMAILS_COUNT;
  const hasTooManyRecipients = preparedReportEmails.length > MAX_REPORT_RECIPIENT_EMAILS_COUNT;
  const isRequestDisabled =
    loading ||
    isReportAddressesLoading ||
    !startDate ||
    !endDate ||
    Boolean(dateRangeError) ||
    !selectedMediafileId ||
    selectedAddressIds.length === 0 ||
    preparedReportEmails.length === 0 ||
    invalidExternalRecipientEmails.length > 0 ||
    hasTooManyExternalRecipients ||
    hasTooManyRecipients;

  const onDateRangeChange = useCallback((nextStartDate: Date | null, nextEndDate: Date | null) => {
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
  }, []);
  const onSelectedAddressIdsChange = useCallback((addressIds: string[]) => {
    setIsAddressSelectionTouched(true);
    setSelectedAddressIds(addressIds);
  }, []);
  const onSelectedRecipientIdsChange = useCallback(
    (recipientIds: string[]) => setSelectedRecipientIds(recipientIds),
    []
  );
  const onSelectedMediafileIdChange = useCallback((mediafileId: string) => setSelectedMediafileId(mediafileId), []);
  const onExternalRecipientEmailsChange = useCallback((emails: string[]) => setExternalRecipientEmails(emails), []);
  const onShowExternalRecipientsInput = useCallback(() => setIsExternalRecipientsInputVisible(true), []);
  const onModalClose = useCallback(() => close(), [close]);

  const onRequestSubmit = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    const dateRangeError = getDailyReportDateRangeError(startDate, endDate);

    if (dateRangeError) {
      toast.error(dateRangeError);

      return;
    }

    if (!selectedMediafileId) {
      toast.error(TEXTS.PROMO_REQUIRED_ERROR);

      return;
    }

    if (selectedAddressIds.length === 0) {
      toast.error(TEXTS.ADDRESSES_EMPTY_ERROR);

      return;
    }

    if (invalidExternalRecipientEmails.length) {
      toast.error(TEXTS.EXTERNAL_RECIPIENTS_INVALID_ERROR);

      return;
    }

    if (hasTooManyExternalRecipients) {
      toast.error(TEXTS.EXTERNAL_RECIPIENTS_MAX_COUNT_ERROR);

      return;
    }

    if (hasTooManyRecipients) {
      toast.error(TEXTS.RECIPIENTS_MAX_COUNT_ERROR);

      return;
    }

    const input = buildDailyReportInput({
      companyId,
      endDate,
      externalRecipientEmails,
      mediafileId: selectedMediafileId,
      selectedAddressIds,
      selectedRecipientEmails,
      startDate,
    });

    if (!input) {
      toast.error(TEXTS.RECIPIENTS_EMPTY_ERROR);

      return;
    }

    try {
      const result = await mutation({ variables: { input } });
      const reportResult = result.data?.result as CompanyAdCampaignsByDaysEarlyReportMutation | null | undefined;

      if (!reportResult?.ok) {
        toast.error(DEFAULT_ERROR_MESSAGE);

        return;
      }

      if (!reportResult.isCreated) {
        toast.info(TEXTS.REQUEST_EXISTS_NOTIFICATION, { duration: 15000 });

        return;
      }

      close();
      toast.info(TEXTS.REQUEST_SUCCESS_NOTIFICATION, { duration: 15000 });
      setTimeout(refetchReports, FIRST_REFETCH_INTERVAL);
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  }, [
    close,
    companyId,
    endDate,
    externalRecipientEmails,
    hasTooManyExternalRecipients,
    hasTooManyRecipients,
    invalidExternalRecipientEmails.length,
    mutation,
    refetchReports,
    selectedAddressIds,
    selectedMediafileId,
    selectedRecipientEmails,
    startDate,
  ]);

  return {
    calendarMaxDate,
    endDate,
    externalRecipientEmails,
    isExternalRecipientsInputVisible,
    isReportAddressesLoading,
    isRequestDisabled,
    loading,
    maxDate,
    minDate,
    recipients,
    reportAddresses,
    reportAddressTags,
    selectedAddressIds,
    selectedMediafileId,
    selectedRecipientIds,
    startDate,
    onDateRangeChange,
    onExternalRecipientEmailsChange,
    onModalClose,
    onRequestSubmit,
    onSelectedAddressIdsChange,
    onSelectedMediafileIdChange,
    onSelectedRecipientIdsChange,
    onShowExternalRecipientsInput,
  };
};
